import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useSidebarPageLayoutActions} from '@github-ui/layout-primitives/SidebarPageLayout'
import type {SidebarLink} from '@github-ui/saved-views'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {useAnalytics} from '@github-ui/use-analytics'
import {PlusIcon, StackIcon} from '@primer/octicons-react'
import {ActionList, IconButton, NavList, Truncate} from '@primer/react'
import {useId} from 'react'

import {getHandlerNavItemClick} from '../utils/sidebar-link'
import styles from './ReposShortcutSidebar.module.css'
import {SidebarLinkIcon} from './SidebarLinkIcon'

interface ReposShortcutSidebarProps {
  shortcuts: SidebarLink[]
  onSelectLink: (linkItem: SidebarLink) => void
  selectedLink: string
  onCreateView: () => void
}

interface ReposShortcutFilteredSidebarProps extends ReposShortcutSidebarProps {
  sendAnalyticsEvent: SendAnalyticsEventFunction
}

const ReposShortcutFilteredSidebar = ({
  shortcuts,
  selectedLink,
  onSelectLink,
  onCreateView,
  sendAnalyticsEvent,
}: ReposShortcutFilteredSidebarProps) => {
  const {closeDialog} = useSidebarPageLayoutActions()

  const handleOnCreateView = (cb?: () => void) => {
    closeDialog()
    cb?.()
    onCreateView()
  }

  const _shortcuts =
    shortcuts.length === 0 ? (
      <li className={styles.emptyStateItem}>
        <span className="text-small">No saved views</span>
      </li>
    ) : (
      shortcuts.map(linkItem => (
        <CollapsibleNavList.Item
          aria-current={selectedLink === linkItem.id && 'page'}
          key={linkItem.id}
          href={`?q=${linkItem.query}`}
          onClick={getHandlerNavItemClick({
            linkItem,
            sendAnalyticsEvent,
            onSelectLink: selectedShortcut => {
              closeDialog()
              onSelectLink(selectedShortcut)
            },
          })}
        >
          <CollapsibleNavList.LeadingVisual>
            <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
          </CollapsibleNavList.LeadingVisual>
          <Truncate title={linkItem.name} maxWidth="min(100%, 32ch)">
            {linkItem.name}
          </Truncate>
        </CollapsibleNavList.Item>
      ))
    )
  return (
    <CollapsibleNavList aria-label="Saved views" className={styles.ReposShortcutSidebar}>
      <CollapsibleNavList.Divider />
      <CollapsibleNavList.Group
        title="Views"
        icon={StackIcon}
        expandedHeading
        popoverSize="medium"
        headingLevel="h2"
        trailingAction={({close}) => (
          <ActionList.GroupHeading.TrailingAction
            icon={PlusIcon}
            label="Create view"
            tooltipDirection="e"
            onClick={() => {
              handleOnCreateView(close)
            }}
          />
        )}
      >
        {_shortcuts}
      </CollapsibleNavList.Group>
    </CollapsibleNavList>
  )
}

export function ReposShortcutSidebar({shortcuts, selectedLink, onSelectLink, onCreateView}: ReposShortcutSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const createViewButtonId = useId()

  if (isFeatureEnabled('repos_finder_sidebar_layout')) {
    return (
      <ReposShortcutFilteredSidebar
        shortcuts={shortcuts}
        selectedLink={selectedLink}
        onSelectLink={onSelectLink}
        onCreateView={onCreateView}
        sendAnalyticsEvent={sendAnalyticsEvent}
      />
    )
  }

  return (
    <div>
      <div className={styles.headerRow} id={createViewButtonId}>
        <span className={styles.headerRowText}>Views</span>
        <IconButton icon={PlusIcon} size="small" variant="invisible" onClick={onCreateView} aria-label="Create view" />
      </div>
      {shortcuts.length === 0 ? (
        <div className={styles.noSavedViews}>No saved views</div>
      ) : (
        <NavList aria-labelledby={createViewButtonId} className={styles.savedViewsList}>
          {shortcuts.map(linkItem => (
            <NavList.Item
              aria-current={selectedLink === linkItem.id && 'page'}
              key={linkItem.id}
              href={`?q=${linkItem.query}`}
              onClick={getHandlerNavItemClick({onSelectLink, sendAnalyticsEvent, linkItem})}
            >
              <NavList.LeadingVisual>
                <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
              </NavList.LeadingVisual>
              <Truncate title={linkItem.name} maxWidth="100%">
                {linkItem.name}
              </Truncate>
            </NavList.Item>
          ))}
        </NavList>
      )}
    </div>
  )
}
